import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import * as Sentry from '@sentry/react';
import { fetchAuthSession, signOut as awsAmplifySignOut } from 'aws-amplify/auth';
import awsConfig from 'aws-exports';
import { oAuthStorage } from 'core/components/auth';
import { isLocalhost } from 'core/helpers/misc';
import { track, trackAndWait } from 'core/helpers/analytics';
import { describeDeepLink } from './deep-link-analytics';

const redirectUri = `${window.location.origin}/oauth-response`;
const cognitoOAuthDomain = `https://${
  import.meta.env.VITE_BRANCH_COGNITO_OAUTH_DOMAIN
}.auth.us-east-1.amazoncognito.com`;
const authorizeEndpoint = `${cognitoOAuthDomain}/oauth2/authorize`;
const tokenEndpoint = `${cognitoOAuthDomain}/oauth2/token`;
const logoutEndpoint = `${cognitoOAuthDomain}/logout`;

const resolveIdentityProvider = (impersonation) => {
  if (impersonation) {
    return isLocalhost
      ? import.meta.env.VITE_LOCALHOST_IMPERSONATION_DESCOPE_PROVIDER_NAME
      : import.meta.env.VITE_HOSTED_APP_IMPERSONATION_DESCOPE_PROVIDER_NAME;
  }
  return isLocalhost
    ? import.meta.env.VITE_LOCALHOST_DESCOPE_PROVIDER_NAME
    : import.meta.env.VITE_HOSTED_APP_DESCOPE_PROVIDER_NAME;
};

const sha256 = async (str) => window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));

const generateNonce = async () => {
  const hash = await sha256(window.crypto.getRandomValues(new Uint32Array(4)).toString());
  const hashArray = Array.from(new Uint8Array(hash));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

const base64URLEncode = (string) =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(string)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

// This function creates all necessary parameters and hits /authorize endpoint of Cognito to start the OAuth flow
// AWS Docs: https://docs.aws.amazon.com/cognito/latest/developerguide/authorization-endpoint.html
export async function signIn({ impersonation = false } = {}) {
  const state = await generateNonce();
  const pkce_key = await generateNonce();
  const code_challenge = base64URLEncode(await sha256(pkce_key));

  oAuthStorage.setState(state);
  oAuthStorage.setPKCE(pkce_key);
  oAuthStorage.setInitiatedAuthStatus(true);
  oAuthStorage.setImpersonation(impersonation);

  const code_challenge_method = 'S256';
  const scopesString = 'email openid profile phone aws.cognito.signin.user.admin';

  const queryParams = {
    response_type: 'code',
    code_challenge,
    code_challenge_method,
    state,
    redirect_uri: redirectUri,
    client_id: awsConfig.userPoolWebClientId,
    identity_provider: resolveIdentityProvider(impersonation),
    scope: scopesString
  };

  const queryString = Object.entries(queryParams)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

  const URL = `${authorizeEndpoint}?${queryString}`;
  window.open(URL, '_self');
}

const getCognitoTokens = (code, code_verifier) => {
  const queryParams = {
    grant_type: 'authorization_code',
    client_id: awsConfig.userPoolWebClientId,
    redirect_uri: redirectUri,
    code,
    code_verifier
  };

  const queryString = Object.entries(queryParams)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

  return fetch(tokenEndpoint, {
    method: 'POST',
    headers: new Headers({ 'content-type': 'application/x-www-form-urlencoded' }),
    body: queryString
  });
};

// This solution is based on https://github.com/aws-amplify/amplify-js/issues/8632
const handleOAuthResponse = async (payload) => {
  const AccessToken = new AmazonCognitoIdentity.CognitoAccessToken({
    AccessToken: payload.access_token
  });

  const IdToken = new AmazonCognitoIdentity.CognitoIdToken({
    IdToken: payload.id_token
  });

  const RefreshToken = new AmazonCognitoIdentity.CognitoRefreshToken({
    RefreshToken: payload.refresh_token
  });

  const sessionData = {
    IdToken,
    AccessToken,
    RefreshToken
  };

  const session = new AmazonCognitoIdentity.CognitoUserSession(sessionData);

  const poolData = {
    UserPoolId: import.meta.env.VITE_STAFF_USER_POOL_ID,
    ClientId: awsConfig.userPoolWebClientId
  };

  const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

  const cognitoUser = new AmazonCognitoIdentity.CognitoUser({
    Username: AccessToken.payload.username,
    Pool: userPool
  });

  // Session first: the code is already spent, so nothing past this point may discard the tokens
  cognitoUser.setSignInUserSession(session);

  const { payload: userData } = IdToken;
  try {
    // The sign-in completion event (~194 saved Amplitude queries; the Descope-connector events
    // fire before the token exchange and can't replace it — see PR #35733). Awaited so
    // redirectAfterSignIn's window.location.replace doesn't drop it from the Segment queue.
    // No identify() here: setupUserSession (auth.jsx) sends the full trait set after the reload.
    await trackAndWait('Staff Sign In', {
      username: userData['cognito:username'],
      email: userData.email
    });
  } catch (analyticsError) {
    Sentry.captureException(analyticsError, { tags: { flow: 'staff-sign-in' } });
  }

  redirectAfterSignIn();
};

let hasRedirectedAfterSignIn = false;

export const redirectAfterSignIn = ({ requireDeepLink = false } = {}) => {
  if (hasRedirectedAfterSignIn) {
    return;
  }
  const deepLink = oAuthStorage.getDeepLink();
  if (!deepLink && requireDeepLink) {
    return;
  }
  hasRedirectedAfterSignIn = true;
  window.location.replace(deepLink || '/');
};

const hasValidAmplifySession = async () => {
  try {
    return !!(await fetchAuthSession()).tokens?.idToken;
  } catch {
    return false;
  }
};

// Full reset to a fresh sign-in. Used by the error-dialog actions ("Try again" AND the
// backdrop/Esc close): clearing the in-flight OAuth storage stops a reused /signin?code=... or
// /oauth-response?code=... URL from replaying the spent flow (#35646, #35647).
export const restartSignIn = () => {
  oAuthStorage.clearAll();
  window.location.replace('/signin');
};

// A failed or replayed /oauth-response callback must never leave the user on the loader
// (#35646): fall back to the session Amplify may already have, otherwise restart sign-in.
export const recoverFromFailedSignIn = async () => {
  if (await hasValidAmplifySession()) {
    redirectAfterSignIn();
    return;
  }
  restartSignIn();
};

export const setCognitoUserSession = async (code, returnedState) => {
  const { oAuthState: expectedState } = oAuthStorage.getState();
  const codeVerifier = oAuthStorage.getPKCE();
  try {
    if (!expectedState || returnedState !== expectedState) {
      throw new Error('OAuth state mismatch');
    }
    const response = await getCognitoTokens(code, codeVerifier);
    const tokens = await response.json();
    if (!response.ok || tokens.error) {
      // e.g. 400 {"error":"invalid_grant"} when the code was already spent or the verifier is gone
      throw new Error(tokens.error || `Token exchange failed with status ${response.status}`);
    }
    await handleOAuthResponse(tokens);
    return { status: 'success' };
  } catch (error) {
    track('Staff Sign In Failed', {
      failureSource: 'token-exchange',
      errorMessage: error?.message,
      ...describeDeepLink(oAuthStorage.peekDeepLink())
    });
    // With the stack — errorMessage alone couldn't identify the script behind the
    // "Wasm code generation disallowed by embedder" failures (#35646)
    Sentry.captureException(error, { tags: { flow: 'staff-sign-in' } });
    if (await hasValidAmplifySession()) {
      redirectAfterSignIn();
      return { status: 'recovered' };
    }
    // The caller shows the failure dialog; clear the flow state so "Try again" starts fresh
    oAuthStorage.clearAll();
    return { status: 'failed' };
  }
};

export const signOut = async () => {
  await awsAmplifySignOut();
  oAuthStorage.clearAll();
  const queryParams = {
    client_id: awsConfig.userPoolWebClientId,
    logout_uri: window.location.origin
  };

  const queryString = Object.entries(queryParams)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

  const URL = `${logoutEndpoint}?${queryString}`;
  console.log('URL', URL);
  window.open(URL, '_self');
};
